import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import {
  Ban,
  CheckCircle2,
  PackageCheck,
  Pencil,
  Plus,
  Printer,
  ShoppingCart,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type { PurchaseSummary } from '@aeris/contracts'
import { formatMoney } from '@aeris/money'
import { formatDocumentNumber } from '@aeris/printing'
import { parseAmount, ZERO_AMOUNT } from '@aeris/purchasing'
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  InlineAlert,
  ListToolbar,
  Page,
  PageHeader,
  Pagination,
  QueryState,
  ResultCount,
  RowActions,
  RowOpener,
  SearchInput,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  SortableHeaderCell,
  TableHeaderCell,
  TableRow,
  VisuallyHidden,
} from '@aeris/ui'

import { PartyPicker } from '../components/party-picker.tsx'
import { PurchaseDetailDialog } from '../components/purchase-detail-dialog.tsx'
import { PurchaseForm } from '../components/purchase-form.tsx'
import { PurchaseReceiptDialog } from '../components/purchase-receipt-dialog.tsx'
import { DocumentStatusBadge } from '../components/status-badge.tsx'
import { StatusLegend } from '../components/status-legend.tsx'
import {
  ApiError,
  fetchPurchase,
  fetchPurchases,
  transitionPurchase,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { formatDate } from '../lib/date-format.ts'
import { droppedMessage } from '../lib/label-queue.ts'
import { queuePurchaseLabels } from '../lib/purchase-labels.ts'
import { STATUS_LABELS, TRANSITION_LABELS } from '../lib/purchasing-labels.ts'
import {
  PURCHASE_LEGEND,
  PURCHASE_STATUS_META,
} from '../lib/purchasing-status-meta.tsx'
import {
  labelsFromPurchase,
  labelsFromReceipt,
  type LabelDemand,
} from '../lib/received-labels.ts'
import { useDebounced } from '../lib/use-debounced.ts'
import { sortKey, useTableSort } from '../lib/use-table-sort.ts'

/**
 * Compras.
 *
 * Its own screen and not a mode of Vendas: ADR-0020 keeps buying and selling as
 * separate documents because four of the five apparent symmetries between them
 * differ in kind rather than in sign, and one screen for both would advertise a
 * symmetry the product does not have.
 *
 * Two numbers are shown per row on purpose. Ours is what we filed the purchase
 * under; the supplier's is what is printed on their invoice, and it is the one
 * somebody has in their hand when they come looking for it.
 *
 * "Receber" is a separate control from the status buttons, gated on a separate
 * permission. Receiving is not a status somebody sets — it is a delivery with
 * quantities, and it is the write that moves stock and rewrites what every unit
 * is worth.
 */

const PAGE_SIZE = 25

export const ComprasPage = (): ReactNode => {
  const { allows, session } = useAuth()
  const navigate = useNavigate()
  /*
   * As etiquetas do que acabou de chegar (F124, entrada de compra).
   *
   * Uma oferta, e não um enfileiramento automático: nem todo recebimento vai
   * para a prateleira etiquetado, e uma fila que enche sozinha a cada nota é
   * uma fila que alguém tem de esvaziar antes de imprimir o que queria.
   */
  const [labelDemand, setLabelDemand] = useState<readonly LabelDemand[]>([])
  const [labelError, setLabelError] = useState<string | null>(null)
  const [queueingLabels, setQueueingLabels] = useState(false)
  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<'' | PurchaseSummary['status']>('')
  // "Tudo que comprei do fornecedor X" — a API já filtrava (F132).
  const [supplierPartyId, setSupplierPartyId] = useState('')
  const [page, setPage] = useState(1)

  // The list goes back to the first page when the column changes: a new
  // order with the old offset shows rows that have nothing to do with
  // what was just clicked.
  const sort = useTableSort<'issued_at' | 'number' | 'total_amount'>(
    'issued_at',
    'desc',
    () => {
      setPage(1)
    }
  )
  const [formOpen, setFormOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [receivingId, setReceivingId] = useState<string | null>(null)
  /** A ficha de leitura (F154): clicar na linha, sem tocar em nada. */
  const [viewingId, setViewingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // 300 ms: long enough that a typed word is one request, short enough that the
  // list feels attached to the keyboard.
  const debouncedSearch = useDebounced(search)

  const canWrite = allows('purchasing.write')
  const canReceive = allows('purchasing.receive')
  // Etiqueta pede o preço do catálogo — quem não lê o catálogo não imprime.
  const canLabel = allows('catalog.read')

  const query = useQuery({
    queryKey: [
      'purchases',
      {
        search: debouncedSearch,
        status,
        supplierPartyId,
        page,
        ...sortKey(sort),
      },
    ],
    queryFn: ({ signal }) =>
      fetchPurchases(
        {
          ...(debouncedSearch.trim().length > 0
            ? { search: debouncedSearch.trim() }
            : {}),
          ...(status === '' ? {} : { status }),
          ...(supplierPartyId === '' ? {} : { supplierPartyId }),
          page,
          pageSize: PAGE_SIZE,
          sort: sort.sort,
          direction: sort.direction,
        },
        signal
      ),
    // Keeps the previous page on screen while the next one loads, so the table
    // does not collapse to a spinner on every keystroke.
    placeholderData: (previous) => previous,
  })

  const editing = useQuery({
    queryKey: ['purchase', editingId],
    queryFn: ({ signal }) => fetchPurchase(editingId ?? '', signal),
    enabled: editingId !== null,
  })

  /** The receipt needs the lines, which the list does not carry. */
  const receiving = useQuery({
    queryKey: ['purchase', receivingId],
    queryFn: ({ signal }) => fetchPurchase(receivingId ?? '', signal),
    enabled: receivingId !== null,
  })

  const move = useMutation({
    mutationFn: async (input: {
      document: PurchaseSummary
      to: 'confirmed' | 'cancelled'
      reason?: string | null
    }) =>
      await transitionPurchase(input.document.id, {
        version: input.document.version,
        to: input.to,
        reason: input.reason ?? null,
      }),
    onSuccess: async () => {
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['purchases'] })
    },
    onError: (cause: unknown) => {
      // The refusal is shown, not swallowed: somebody pressing a button and
      // seeing nothing happen cannot tell a business rule from a bug.
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível mover a compra.'
      )
    },
  })

  const total = query.data?.total ?? 0
  const items = query.data?.items ?? []
  /*
   * A situação de quem está sendo visto, tirada da própria lista (F154).
   *
   * É o que decide se a ficha oferece Editar, e sai daqui de graça: a linha
   * clicada já está carregada. Uma consulta nova só para saber se é rascunho
   * seria pedir ao servidor o que está na tela.
   */
  const viewingSummary = items.find((item) => item.id === viewingId) ?? null
  const filtered =
    debouncedSearch.trim().length > 0 || status !== '' || supplierPartyId !== ''

  /* Cancelar pede confirmação e motivo (F132), como em Vendas. */
  const [cancelling, setCancelling] = useState<PurchaseSummary | null>(null)

  const labelCount = labelDemand.reduce((sum, item) => sum + item.labels, 0)

  /*
   * Pôr etiquetas de compra na fila — um caminho só para o que acabou de
   * chegar, a compra reaberta na ficha e a ação da linha (F195). O que não
   * coube na fila é dito aqui e a tela NÃO muda: sair para Etiquetas
   * esconderia o aviso de que faltaram peças.
   */
  const queueLabels = (
    demand: readonly LabelDemand[],
    onQueued?: () => void
  ): void => {
    const tenantId = session?.context?.tenantId ?? null
    if (tenantId === null || demand.length === 0) return
    setQueueingLabels(true)
    setLabelError(null)
    queuePurchaseLabels(tenantId, demand)
      .then(async ({ dropped }) => {
        setLabelDemand([])
        onQueued?.()
        const message = droppedMessage(dropped)
        if (message !== null) {
          setLabelError(message)
          return
        }
        await navigate({ to: '/etiquetas' })
      })
      .catch(() => {
        setLabelError('Não foi possível buscar os produtos para as etiquetas.')
      })
      .finally(() => {
        setQueueingLabels(false)
      })
  }

  /* A compra da linha não traz as linhas dela: a ação busca e etiqueta. */
  const queueLabelsOf = (purchaseId: string): void => {
    setQueueingLabels(true)
    setLabelError(null)
    queryClient
      .fetchQuery({
        queryKey: ['purchase', purchaseId],
        queryFn: ({ signal }) => fetchPurchase(purchaseId, signal),
      })
      .then(({ document }) => {
        const demand = labelsFromPurchase(document.lines)
        if (demand.length === 0) {
          setQueueingLabels(false)
          setLabelError(
            'Nada desta compra chegou ainda — não há o que etiquetar.'
          )
          return
        }
        queueLabels(demand)
      })
      .catch(() => {
        setQueueingLabels(false)
        setLabelError('Não foi possível abrir a compra para as etiquetas.')
      })
  }

  return (
    <Page>
      <PageHeader
        title="Compras"
        description="Pedidos a fornecedores e o que já chegou. O recebimento é o que dá estoque e custo ao produto."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {/* O "?" da barra: as situações desta lista e o que cada uma quer
                dizer (F156). */}
            <StatusLegend entries={PURCHASE_LEGEND} />
            {canWrite ? (
              <>
                {/* A nota do fornecedor vira compra sem digitar item por item. */}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    void navigate({ to: '/importar-nfe' })
                  }}
                >
                  Importar NF-e
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  leadingIcon={<Plus aria-hidden="true" className="h-4 w-4" />}
                  onClick={() => {
                    setEditingId(null)
                    setFormOpen(true)
                  }}
                >
                  Nova compra
                </Button>
              </>
            ) : null}
          </div>
        }
      />

      <ListToolbar>
        <SearchInput
          value={search}
          onChange={(event) => {
            setSearch(event.target.value)
            setPage(1)
          }}
          placeholder="Buscar por número, nota do fornecedor ou fornecedor"
          aria-label="Buscar compras"
        />

        <Select
          value={status}
          aria-label="Situação"
          className="w-48"
          onChange={(event) => {
            setStatus(event.target.value as '' | PurchaseSummary['status'])
            setPage(1)
          }}
        >
          <option value="">Todas as situações</option>
          {(Object.keys(STATUS_LABELS) as PurchaseSummary['status'][]).map(
            (value) => (
              <option key={value} value={value}>
                {STATUS_LABELS[value]}
              </option>
            )
          )}
        </Select>

        <PartyPicker
          value={supplierPartyId}
          role="supplier"
          placeholder="Fornecedor"
          aria-label="Fornecedor"
          className="w-56"
          onChange={(value) => {
            setSupplierPartyId(value)
            setPage(1)
          }}
        />
      </ListToolbar>

      {/*
        As etiquetas do que acabou de chegar (F124), perto da lista — antes
        ficavam depois da paginação, fora da vista de quem acabou de receber.
      */}
      {labelDemand.length === 0 || !allows('catalog.read') ? null : (
        <InlineAlert
          tone="success"
          title={`Recebido. ${String(labelCount)} ${labelCount === 1 ? 'etiqueta' : 'etiquetas'} do que chegou agora.`}
          action={
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setLabelDemand([])
                  setLabelError(null)
                }}
              >
                Agora não
              </Button>
              <Button
                variant="primary"
                size="sm"
                loading={queueingLabels}
                leadingIcon={<Printer aria-hidden="true" className="h-4 w-4" />}
                onClick={() => {
                  queueLabels(labelDemand)
                }}
              >
                Imprimir etiquetas do que chegou
              </Button>
            </>
          }
        >
          {labelError}
        </InlineAlert>
      )}

      {labelDemand.length === 0 && labelError !== null ? (
        <InlineAlert tone="danger">{labelError}</InlineAlert>
      ) : null}

      {receivingId !== null && receiving.isPending ? (
        <InlineAlert tone="info">Carregando os itens da compra…</InlineAlert>
      ) : null}

      <ResultCount
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        loading={query.isPending}
        none="Nenhuma compra encontrada"
      />

      {error === null ? null : <InlineAlert tone="danger">{error}</InlineAlert>}

      <QueryState
        isPending={query.isPending}
        isError={query.isError}
        error={query.error}
        onRetry={() => {
          void query.refetch()
        }}
        what="as compras"
        isEmpty={items.length === 0}
        filtered={filtered}
        onClearFilters={() => {
          setSearch('')
          setSupplierPartyId('')
          setStatus('')
          setPage(1)
        }}
        empty={
          <EmptyState
            icon={<ShoppingCart aria-hidden="true" className="h-8 w-8" />}
            title="Nenhuma compra ainda"
            description={
              canWrite
                ? 'Registre a primeira compra. É o recebimento dela que vai dar custo aos produtos.'
                : 'Você não tem permissão para registrar compras.'
            }
          />
        }
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <SortableHeaderCell kind="number" {...sort.column('number')}>
                  Nº
                </SortableHeaderCell>
                <TableHeaderCell>Nota do fornecedor</TableHeaderCell>
                <TableHeaderCell>Fornecedor</TableHeaderCell>
                <TableHeaderCell>Comprador</TableHeaderCell>
                <SortableHeaderCell kind="date" {...sort.column('issued_at')}>
                  Emissão
                </SortableHeaderCell>
                <TableHeaderCell>Situação</TableHeaderCell>
                <SortableHeaderCell numeric {...sort.column('total_amount')}>
                  Total
                </SortableHeaderCell>
                {canWrite || canReceive || canLabel ? (
                  <TableHeaderCell>
                    <VisuallyHidden>Ações</VisuallyHidden>
                  </TableHeaderCell>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((document) => {
                const canEdit = canWrite && document.status === 'draft'
                const canConfirm =
                  canWrite && document.allowedTransitions.includes('confirmed')
                // Receber tem permissão e diálogo próprios: quem negocia com o
                // fornecedor e quem conta o que desceu do caminhão costumam
                // ser pessoas diferentes.
                const canReceiveThis =
                  canReceive && document.status === 'confirmed'
                const primaryKind = canReceiveThis
                  ? 'receive'
                  : canEdit
                    ? 'edit'
                    : canConfirm
                      ? 'confirm'
                      : null

                const primary =
                  primaryKind === 'receive' ? (
                    <Button
                      variant="primary"
                      size="sm"
                      leadingIcon={
                        <PackageCheck
                          aria-hidden="true"
                          className="h-3.5 w-3.5"
                        />
                      }
                      onClick={() => {
                        setReceivingId(document.id)
                      }}
                    >
                      Receber
                    </Button>
                  ) : primaryKind === 'edit' ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      leadingIcon={
                        <Pencil aria-hidden="true" className="h-3.5 w-3.5" />
                      }
                      onClick={() => {
                        setEditingId(document.id)
                        setFormOpen(true)
                      }}
                    >
                      Editar
                    </Button>
                  ) : primaryKind === 'confirm' ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={move.isPending}
                      onClick={() => {
                        move.mutate({ document, to: 'confirmed' })
                      }}
                    >
                      {TRANSITION_LABELS.confirmed}
                    </Button>
                  ) : undefined

                return (
                  /*
                   * Clicar na linha abre a FICHA (F154).
                   *
                   * Aqui isso importa mais do que nas outras telas: a coluna
                   * de ações só existe para quem pode escrever ou receber, de
                   * modo que quem tem só `purchasing.read` não tinha nenhum
                   * clique possível nesta lista.
                   */
                  <TableRow
                    key={document.id}
                    onOpen={() => {
                      setViewingId(document.id)
                    }}
                  >
                    <TableCell className="font-(--font-mono) whitespace-nowrap">
                      <RowOpener
                        onOpen={() => {
                          setViewingId(document.id)
                        }}
                      >
                        {formatDocumentNumber(document.number)}
                      </RowOpener>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-(--text-secondary)">
                      {document.supplierDocumentNumber ?? '—'}
                    </TableCell>
                    <TableCell>
                      {document.supplierName}
                      <span className="block text-(length:--text-xs) text-(--text-muted)">
                        {document.lineCount === 1
                          ? '1 item'
                          : `${String(document.lineCount)} itens`}
                      </span>
                    </TableCell>
                    <TableCell className="text-(--text-secondary)">
                      {document.buyerName ?? '—'}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-(--text-secondary)">
                      {formatDate(document.issuedAt)}
                    </TableCell>
                    <TableCell>
                      <DocumentStatusBadge
                        meta={PURCHASE_STATUS_META[document.status]}
                      />
                      {/* A confirmed purchase with an expected date that has
                          passed is the thing somebody actually wants to see in
                          this list: a delivery that is late. */}
                      {document.status === 'confirmed' &&
                      document.expectedAt !== null &&
                      new Date(document.expectedAt) < new Date() ? (
                        <Badge tone="danger" className="ml-2">
                          Atrasada
                        </Badge>
                      ) : null}
                    </TableCell>
                    <TableCell numeric>
                      {formatMoney(
                        parseAmount(document.totalAmount) ?? ZERO_AMOUNT
                      )}
                    </TableCell>
                    {canWrite || canReceive || canLabel ? (
                      <TableCell>
                        <RowActions
                          name={`compra nº ${formatDocumentNumber(document.number)}`}
                          primary={primary}
                          items={[
                            primaryKind !== 'edit' &&
                              canEdit && {
                                label: 'Editar',
                                icon: (
                                  <Pencil
                                    aria-hidden="true"
                                    className="h-4 w-4"
                                  />
                                ),
                                onSelect: () => {
                                  setEditingId(document.id)
                                  setFormOpen(true)
                                },
                              },
                            primaryKind !== 'confirm' &&
                              canConfirm && {
                                label: TRANSITION_LABELS.confirmed,
                                icon: (
                                  <CheckCircle2
                                    aria-hidden="true"
                                    className="h-4 w-4"
                                  />
                                ),
                                disabled: move.isPending,
                                onSelect: () => {
                                  move.mutate({ document, to: 'confirmed' })
                                },
                              },
                            canLabel &&
                              document.status === 'received' && {
                                label: 'Imprimir etiquetas',
                                icon: (
                                  <Printer
                                    aria-hidden="true"
                                    className="h-4 w-4"
                                  />
                                ),
                                disabled: queueingLabels,
                                onSelect: () => {
                                  queueLabelsOf(document.id)
                                },
                              },
                            canWrite &&
                              document.allowedTransitions.includes(
                                'cancelled'
                              ) && {
                                label: 'Cancelar',
                                danger: true,
                                icon: (
                                  <Ban aria-hidden="true" className="h-4 w-4" />
                                ),
                                onSelect: () => {
                                  setCancelling(document)
                                },
                              },
                          ]}
                        />
                      </TableCell>
                    ) : null}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </QueryState>

      <Pagination
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
      />

      {/* Mounted only while open, and keyed by the document being edited, so
          the form starts from the right values without an effect that resets
          them. React's own answer to "reset state when a prop changes". */}
      {formOpen && (editingId === null || editing.data !== undefined) ? (
        <PurchaseForm
          key={editingId ?? 'new'}
          document={editingId === null ? undefined : editing.data?.document}
          onClose={() => {
            setFormOpen(false)
            setEditingId(null)
          }}
        />
      ) : null}

      <ConfirmDialog
        open={cancelling !== null}
        onOpenChange={(next) => {
          if (!next) setCancelling(null)
        }}
        title="Cancelar compra"
        description={
          cancelling === null
            ? ''
            : `Cancelar a compra nº ${formatDocumentNumber(cancelling.number)} de ${cancelling.supplierName}? Ela deixa de esperar entrega, e o cancelamento fica no histórico com o motivo.`
        }
        confirmLabel="Cancelar compra"
        cancelLabel="Voltar"
        reason={{
          label: 'Motivo',
          placeholder: 'Ex.: fornecedor sem a peça',
          required: true,
        }}
        loading={move.isPending}
        onConfirm={(reason) => {
          if (cancelling === null) return
          move.mutate(
            {
              document: cancelling,
              to: 'cancelled',
              reason: reason?.trim() ?? null,
            },
            {
              onSettled: () => {
                setCancelling(null)
              },
            }
          )
        }}
      />

      {receivingId !== null && receiving.data !== undefined ? (
        <PurchaseReceiptDialog
          key={receivingId}
          document={receiving.data.document}
          onClose={() => {
            setReceivingId(null)
          }}
          onReceived={(lines) => {
            setLabelDemand(labelsFromReceipt(lines))
          }}
        />
      ) : null}

      {viewingId === null ? null : (
        <PurchaseDetailDialog
          purchaseId={viewingId}
          onClose={() => {
            setViewingId(null)
          }}
          /*
           * Editar sai da ficha só quando a tela deixaria editar pela linha:
           * `purchasing.write` E rascunho. Uma compra confirmada já virou
           * custo e estoque a receber.
           */
          {...(canLabel
            ? {
                onPrintLabels: (demand: readonly LabelDemand[]) => {
                  queueLabels(demand, () => {
                    setViewingId(null)
                  })
                },
                printingLabels: queueingLabels,
              }
            : {})}
          {...(canWrite && viewingSummary?.status === 'draft'
            ? {
                onEdit: () => {
                  setEditingId(viewingId)
                  setFormOpen(true)
                  setViewingId(null)
                },
              }
            : {})}
        />
      )}
    </Page>
  )
}
